import { Inject, Injectable } from '@angular/core';
import { BaseHttpService } from '../../../../modules/fwcore';
import { HttpClient } from '@angular/common/http';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { EMPTY, Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import urlJoin from 'url-join';
import { Prompt } from '../../prompt-library/models';
import { ChatModel } from '../models/chat-model.interface';
import { ChatSettings } from '../models/chat-settings.interface';
import { ChatThreadHistory } from '../models/chat-thread-history.interface';
import { ChatThreadRequest, ChatNewThreadRequest } from '../models/chat-thread-request.interface';
import { ChatNewThreadResponse, ChatThreadResponse } from '../models/chat-thread-response.interface';
import { ConfigService } from '../../../../../services/config.service';

@Injectable({
  providedIn: 'root'
})
export class ChatService extends BaseHttpService {
  private newBaseUrl: string = '';
  private newApiKey: string = '';
  private configInitialized: boolean = false;
  private configInitPromise: Promise<void> | null = null;

  constructor(http: HttpClient
    , @Inject(OKTA_AUTH) protected auth: OktaAuth
    , @Inject('CONFIG_SETTINGS') private config: any
    , private configService: ConfigService) {

    super(http, auth);

    // Initialize config asynchronously
    this.initializeConfig();
  }

  private async initializeConfig(): Promise<void> {
    if (this.configInitPromise) {
      return this.configInitPromise;
    }

    this.configInitPromise = (async () => {
      try {
        // Load chat config
        const chatConfig = await this.configService.getConfig('chat');
        this.baseUrl = chatConfig.apiUrl;
        this.apiKey = chatConfig.apiKey;

        // Load chatagents config
        const chatagentsConfig = await this.configService.getConfig('chatagents');
        this.newBaseUrl = chatagentsConfig.apiUrl;
        this.newApiKey = chatagentsConfig.apiKey;

        this.configInitialized = true;
      } catch (error) {
        console.error('Error initializing ChatService config:', error);
        // Fallback to appconfig.json if available
        const configSettings = this.config['chat'];
        if (configSettings) {
          this.baseUrl = configSettings['apiUrl'] || '';
          this.apiKey = configSettings['apiKey'] || '';
        }
        const newConfigSettings = this.config['chatagents'];
        if (newConfigSettings) {
          this.newBaseUrl = newConfigSettings['apiUrl'] || '';
          this.newApiKey = newConfigSettings['apiKey'] || '';
        }
        this.configInitialized = true;
      }
    })();

    return this.configInitPromise;
  }

  private ensureConfigInitialized(): Observable<void> {
    if (this.configInitialized) {
      return new Observable(observer => {
        observer.next();
        observer.complete();
      });
    }
    return from(this.initializeConfig());
  }

  getModelsList(): Observable<ChatModel[]> {
    return this.ensureConfigInitialized().pipe(
      switchMap(() => {
        var token = this.getToken();
        var url = urlJoin(this.baseUrl, 'ai/chat/models');
        return this.getItems<ChatModel>(url, this.getHttpBearerHeaders(token ?? ''));
      })
    );
  }

  getUserSettings(): Observable<ChatSettings> {
    return this.ensureConfigInitialized().pipe(
      switchMap(() => {
        var token = this.getToken();
        var url = urlJoin(this.baseUrl, 'ai/chat/settings');
        return this.getItem<ChatSettings>(url, this.getHttpBearerHeaders(token ?? ''));
      })
    );
  }

  setUserSettings(settings: ChatSettings) {
    return this.ensureConfigInitialized().pipe(
      switchMap(() => {
        var token = this.getToken();
        var url = urlJoin(this.baseUrl, 'ai/chat/settings');
        return this.saveItem(settings, true, url, this.getHttpBearerHeaders(token ?? ''));
      })
    );
  }

  public getChatItem(id: string): Observable<ChatThreadHistory> {
    return this.ensureConfigInitialized().pipe(
      switchMap(() => {
        var token = this.getToken() ?? '';
        var url = urlJoin(this.baseUrl, 'ai/chat/history', id);
        return this.getItem<ChatThreadHistory>(url, this.getHttpBearerHeaders(token));
      })
    );
  }

  public getChatHistoryStub(): Observable<ChatThreadHistory[]> {
    return this.ensureConfigInitialized().pipe(
      switchMap(() => {
        var token = this.getToken();
        var url = urlJoin(this.baseUrl, 'ai/chat/historystub');
        return this.getItems<ChatThreadHistory>(url, this.getHttpBearerHeaders(token ?? ''));
      })
    );
  }

  async deleteChatThread(threadId: string): Promise<any> {
    await this.initializeConfig();
    var token = this.getToken() ?? '';
    var url = urlJoin(this.baseUrl, 'ai/chat/historyitem', threadId);
    return this.deleteAny(url, this.getHttpBearerHeaders(token));
  }

  public sendRequest(req: ChatThreadRequest): Observable<ChatThreadResponse> {
    if (!req) return EMPTY;

    return this.ensureConfigInitialized().pipe(
      switchMap(() => {
        const formData = new FormData();

        if (req.documents?.length) {
          req.documents.forEach(doc => {
            formData.append('documents', doc);
          });
        }
        else {
          formData.append('documents', '');
        }

        formData.append('text', req.text || '');
        formData.append('threadId', req.threadId || '');
        formData.append('modelId', req.model);
        formData.append('temperature', req.temperature.toString());
        formData.append('chatRequestTimestamp',
          req.chatRequestTimestamp?.toUTCString() || new Date().toISOString());

        var token = this.getToken() ?? '';
        var url = urlJoin(this.baseUrl, 'ai/chat/ask');
        return this.postAnyTyped<ChatThreadResponse>(formData, url, this.getHttpOptionsFormData(token));
      })
    );
  }

  public sendNewRequest(req: ChatNewThreadRequest, files: File[]): Observable<ChatNewThreadResponse> {
    if (!req) return EMPTY;

    return this.ensureConfigInitialized().pipe(
      switchMap(() => {
        const formData = new FormData();

        // Add QueryRequest properties to FormData
        formData.append('Prompt', req.prompt);
        formData.append('AgentTypeId', req.agentTypeId);

        if (req.threadId) {
          formData.append('ThreadId', req.threadId);
        }

        if (req.modelId) {
          formData.append('ModelId', req.modelId);
        }

        // Add files to FormData
        files.forEach((file, index) => {
          formData.append('files', file, file.name);
        });

        const token = this.getToken();
        var url = urlJoin(this.newBaseUrl, 'query');

        return this.postAnyTyped<ChatNewThreadResponse>(formData, url, this.getHttpOptionsFormData(token ?? '', this.newApiKey));
      })
    );
  }

}
